import { signalStoreFeature, type, withMethods } from '@ngrx/signals';
import { inject, computed, Signal } from '@angular/core';
import { SignalLoader } from '@aston/foundation';
import { TranslateService } from '@ngx-translate/core';

import { IImportItem } from '../../models';
import { ImportConfigurationStatus, ImportStatus, ImportStep } from '../../enums';

export function withImportDetailLoaders<_>() {
	return signalStoreFeature(
		{
			state: type<{ importItem: IImportItem }>(),
			props: type<{
				importItemLoading: Signal<boolean>,
				reportErrorColumnsLoading: Signal<boolean>,
				completedAccountingLoading: Signal<boolean>,
				blocked: Signal<boolean>,
				importItemError: Signal<string | null>,
				reportErrorColumnsError: Signal<string | null>,
				completedAccountingError: Signal<string | null>,
				importConfigStatus: Signal<ImportConfigurationStatus>,
				configWorkerMessage: Signal<string>,
				importIsDisabled: Signal<boolean>,
			}>(),
		},

		withMethods(store => {
			const translateService = inject(TranslateService);
			
			const currentImportLoader = new SignalLoader(
				store.importItemLoading,
				computed(() => {
					const error = store.importItemError();
					return error && error['toTranslate'] ? translateService.instant(error['label']) : error;
				})
			);
			
			const tabLoader = new SignalLoader(
				computed(() =>
					   store.reportErrorColumnsLoading()
					|| store.completedAccountingLoading()
					|| store.blocked()
				),
				computed(() =>
					   store.reportErrorColumnsError()
					|| store.completedAccountingError()
					|| store.importItemError()
				)
			);
			
			const listLoader = new SignalLoader(
				computed(() =>
					   store.reportErrorColumnsLoading()
					|| store.blocked()
				),
				computed(() =>
					   store.reportErrorColumnsError()
					|| store.importItemError()
				)
			)
			
			const completedAccountingListLoader = new SignalLoader(
				computed(() =>
					   store.completedAccountingLoading()
					|| store.blocked()
				),
				computed(() =>
					   store.completedAccountingError()
					|| store.importItemError()
				)
			)
			
			const importingLoader = new SignalLoader(
				computed(() => store.importItem() && store.importItem().currentStep === ImportStep.Imported && (store.importConfigStatus() === ImportConfigurationStatus.Preparing || store.importItem().status !== ImportStatus.Done)),
				computed(() => store.importItem() && store.importItem().currentStep === ImportStep.Imported && store.importConfigStatus() === ImportConfigurationStatus.Error ? store.configWorkerMessage() : null),
			);
			
			const currentImportOrBlockedLoader = new SignalLoader(
				computed(() =>
					   currentImportLoader.loading()
					|| store.importIsDisabled()
					|| store.blocked()
				),
				currentImportLoader.error
			);

			const synthesisStepLoader = SignalLoader.fromCombinedSignal(
				computed(() => {
					const loader = { loading: false, error: null };
					
					if (store.importItem() && store.importItem().importFileConfiguration) {
						const importFileConfig = store.importItem().importFileConfiguration;
						const importFileConfigStatus = importFileConfig.importConfigurationStatus;
						const currentStep = importFileConfig.currentStep;
						const isOnStepValidationOrHigher = currentStep === ImportStep.ConsistencyCheck;

						loader.loading = importFileConfigStatus === ImportConfigurationStatus.Preparing && isOnStepValidationOrHigher;
						loader.error = importFileConfigStatus === ImportConfigurationStatus.Error && isOnStepValidationOrHigher ? importFileConfig.extraMessage : null;
					}

					return loader;
				})
			);

			return {
				currentImportLoader: () => currentImportLoader,
				tabLoader: () => tabLoader,
				listLoader: () => listLoader,
				completedAccountingListLoader: () => completedAccountingListLoader,
				importingLoader: () => importingLoader,
				synthesisStepLoader: () => synthesisStepLoader,
				currentImportOrBlockedLoader: () => currentImportOrBlockedLoader,
			}
		})
	)
}